import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Star, Heart, Clock } from 'lucide-react';
import { toAbs } from '../api/client';
import { useFavorites } from '../context/FavoritesContext';

const ListingCard = ({ listing }) => {
  const [g1, g2] = listing.gradient;
  const location = useLocation();
  const { isFavorite, toggle } = useFavorites();
  const fav = isFavorite(listing.id);
  const firstImage = (listing.images && listing.images[0]) || listing.image_url;
  const handleClick = () => {
    try { sessionStorage.setItem('inserate_scroll_y', String(window.scrollY)); } catch {}
  };
  const handleFav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(listing.id);
  };
  return (
    <Link
      to={`/inserate/${listing.slug}${location.search || ''}`}
      onClick={handleClick}
      className="group relative bg-[#1a1417] border border-[#2a1f24] rounded-xl overflow-hidden hover:border-[#a83a5b] transition-all duration-300 hover:shadow-xl hover:shadow-[#a83a5b]/10 flex"
    >
      {/* Image Left */}
      <div
        className="relative w-32 sm:w-40 md:w-48 shrink-0 overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${g1} 0%, ${g2} 100%)` }}
      >
        {firstImage ? (
          <img src={toAbs(firstImage)} alt={listing.name}
            className="absolute inset-0 w-full h-full object-cover" loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        ) : (
          <>
            <div className="absolute inset-0 opacity-30 mix-blend-overlay"
              style={{
                backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(0,0,0,0.5) 0%, transparent 50%)'
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="font-serif text-4xl sm:text-5xl md:text-6xl text-white/40 group-hover:text-white/60 transition-colors duration-500">
                {listing.initials}
              </span>
            </div>
          </>
        )}
        {listing.top && (
          <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-black/60 backdrop-blur-sm border border-[#c19a8a]/40 text-[9px] tracking-[0.15em] uppercase text-[#f5d4be] font-semibold inline-flex items-center gap-1">
            <Star className="w-2.5 h-2.5 fill-[#f5d4be]" /> Top
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-black/70 to-transparent" />
      </div>

      {/* Text Right */}
      <div className="flex flex-col flex-1 p-3 sm:p-4 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-serif text-white text-lg sm:text-xl leading-tight truncate flex-1">{listing.name}{listing.age ? <span className="text-[#9a8a83] text-sm ml-1">· {listing.age}</span> : null}</h3>
          <button onClick={handleFav}
            className={`shrink-0 transition-colors ${fav ? 'text-[#ff4d6d]' : 'text-[#5a4d48] hover:text-[#ff8aa3]'}`}
            aria-label={fav ? 'Favorit entfernen' : 'Zu Favoriten hinzufügen'}>
            <Heart className={`w-4 h-4 ${fav ? 'fill-[#ff4d6d]' : ''}`} />
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <Link
            to={`/inserate?category=${encodeURIComponent(listing.category)}`}
            onClick={(e) => e.stopPropagation()}
            className="text-[10px] tracking-wider uppercase text-[#c19a8a] font-medium hover:text-white hover:underline">
            {listing.category}
          </Link>
          <span className="text-[10px] text-[#5a4d48]">·</span>
          <Link
            to={`/inserate?region=${encodeURIComponent(listing.region)}`}
            onClick={(e) => e.stopPropagation()}
            className="text-[11px] text-[#9a8a83] hover:text-white hover:underline inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {listing.region}{listing.city ? ` · ${listing.city}` : ''}
          </Link>
          {typeof listing.distance_km === 'number' && (
            <span className="text-[10px] text-[#f5d4be] bg-[#241a1f] border border-[#2a1f24] rounded-full px-2 py-0.5">
              {listing.distance_km} km
            </span>
          )}
        </div>
        <p className="text-sm text-[#9a8a83] leading-snug line-clamp-2 mb-2 flex-1">{listing.description}</p>
        {listing.service_tags && listing.service_tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {listing.service_tags.slice(0, 3).map((t) => (
              <span key={t} className="px-2 py-0.5 rounded-full bg-[#241a1f] border border-[#2a1f24] text-[10px] text-[#c19a8a]">
                {t}
              </span>
            ))}
            {listing.service_tags.length > 3 && (
              <span className="text-[10px] text-[#7a6b65] px-1">+{listing.service_tags.length - 3}</span>
            )}
          </div>
        )}
        <div className="flex items-center justify-between pt-2 border-t border-[#2a1f24] gap-2">
          <span className="text-xs text-[#7a6b65] inline-flex items-center gap-1 whitespace-nowrap">
            <Clock className="w-3 h-3" /> {listing.offers} Angebote
          </span>
          <span className="text-sm font-medium text-white whitespace-nowrap">{listing.price}</span>
        </div>
      </div>
    </Link>
  );
};

export default ListingCard;
